import { ref, computed, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import {
  GetChainPaymentMethodDocument,
  type GetChainPaymentMethodQuery,
  type TransPickupRequestCreateInput,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import type { ShopOption, DeliveryAvailabilityData, DeliveryArea } from "../types";
import { DELIVERY_KBN_RADIO_ITEMS } from "../constants";

/** フォーム入力状態・バリデーションに必要な依存をまとめた型。 */
interface UseDeliveryMoveRegisterFormParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  transPickupRequestCreateInput: TransPickupRequestCreateInput;
  shopSelectValues_from: Ref<ShopOption>;
  shopSelectValues_to: Ref<ShopOption>;
  shopData_from_address: Ref<string>;
  shopData_from_address1: Ref<string>;
  shopData_to_address: Ref<string>;
  shopData_to_address1: Ref<string>;
  shopToListItems: Ref<ShopOption[]>;
  deliveryAreaName: Ref<string>;
  display_delivery_time: Ref<string>;
  deliveryAvailabilityHeader: Ref<Map<string, string>>;
  deliveryAvailabilityData: DeliveryAvailabilityData[][];
  disp_deliveryAvailabilityData: DeliveryAvailabilityData[][];
  deliveryId_0_Flg: Ref<boolean>;
  deliveryStartHourItem: number[];
  deliveryStartHourValues: Ref<number | null | undefined>;
  deliveryStartMinitValues: Ref<{ label: string; deliveryStartMinit: number } | null | undefined>;
  listDeliveryArea: Ref<DeliveryArea[] | null>;
  paymentRequiredFlg: Ref<boolean>;
}

/** フォーム入力状態・バリデーション・決済方法を扱う composable。 */
export const useDeliveryMoveRegisterForm = (params: UseDeliveryMoveRegisterFormParams) => {
  const {
    errdialog,
    errMessage,
    transPickupRequestCreateInput,
    shopSelectValues_from,
    shopSelectValues_to,
    shopData_from_address,
    shopData_from_address1,
    shopData_to_address,
    shopData_to_address1,
    shopToListItems,
    deliveryAreaName,
    display_delivery_time,
    deliveryAvailabilityHeader,
    deliveryAvailabilityData,
    disp_deliveryAvailabilityData,
    deliveryId_0_Flg,
    deliveryStartHourItem,
    deliveryStartHourValues,
    deliveryStartMinitValues,
    listDeliveryArea,
    paymentRequiredFlg,
  } = params;

  // 配達区分
  const deliveryKbnSelectValues = ref({ index: 1, item: '在庫移動' });

  // 決済方法
  const dummy_payment_amount = ref('');

  // フォームの有効性チェック（masterブランチの動作を再現）
  // vee-validate 4.xのmeta.validは初期状態でfalseのため、独自のバリデーション状態を管理
  const isFormValid = computed(() => {
    // 配送元が選択されているか（nullチェックを含む）
    if (!shopSelectValues_from.value?.shop_code) return false;
    // 配送先が選択されているか（nullチェックを含む）
    if (!shopSelectValues_to.value?.shop_code) return false;
    // 配達希望エリアが選択されているか
    if (!transPickupRequestCreateInput.area_code) return false;
    // 配送希望時間が選択されているか
    if (!transPickupRequestCreateInput.required_delivery_date) return false;
    // 個口数が選択されているか
    if (!transPickupRequestCreateInput.unit) return false;
    return true;
  });

  // 配達区分クリック @click
  async function onClickDeliveryKbn(): Promise<void> {
    transPickupRequestCreateInput.delivery_type = deliveryKbnSelectValues.value.index;

    shopSelectValues_from.value = { shop_code: '', name: '' };
    shopData_from_address.value = '';
    shopData_from_address1.value = '';
    shopSelectValues_to.value = { shop_code: '', name: '' };
    shopData_to_address.value = '';
    shopData_to_address1.value = '';
    shopToListItems.value = [];
    deliveryAreaName.value = '';
    display_delivery_time.value = '';

    deliveryAvailabilityHeader.value = new Map();
    deliveryAvailabilityData.length = 0;
    disp_deliveryAvailabilityData.length = 0;
    deliveryId_0_Flg.value = false;
    deliveryStartHourItem.splice(0);
    deliveryStartHourValues.value = undefined;
    deliveryStartMinitValues.value = undefined;
    listDeliveryArea.value = null;
    transPickupRequestCreateInput.area_code = '';
    shopData_to_address.value = '';
    /*
    if (deliveryKbnSelectValues.value.index == 1) {
      shopSelectValues_from.value = { shop_code: localStorageGetShopCode(), name: '' };
    } else {
      shopSelectValues_to.value = { shop_code: localStorageGetShopCode(), name: '' };
    }
    */
    // await onChangeShopFrom();
    // await onChangeShopTo();
  }

  // 決済方法によって支払金額必須か判定 @change
  async function onChangePaymentMethod(item: string | null | undefined): Promise<void> {
    try {
      const apolloClient = await generateApolloClient();
      const paymentList: ApolloQueryResult<GetChainPaymentMethodQuery> = await apolloClient.query<GetChainPaymentMethodQuery>({
        query: GetChainPaymentMethodDocument,
        variables: {
          chain_code: localStorageGetChainCode(),
          customer_code: localStorageGetCustomerCode()
        }
      });
      const _paymentList = paymentList.data.getChainPaymentMethod;
      paymentRequiredFlg.value = false;
      if (_paymentList && _paymentList.length > 0) {
        for (let i = 0; i < _paymentList.length; i++) {
          if (_paymentList[i]?.payment_method === item && _paymentList[i]?.payment_amount_required === '1') {
            paymentRequiredFlg.value = true;
          }
        }
      }
    } catch (err: unknown) {
      console.error(err);
      errdialog.value = true;
      errMessage.value = err instanceof Error ? err.message : '予期せぬエラーが発生しました。';
      return;
    }
  }

  // 支払金額カンマ処理
  async function commmaMoney(value: string): Promise<void> {
    if (dummy_payment_amount.value !== '') {
      dummy_payment_amount.value = String(value).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    }
  }

  // カンマ削除処理
  async function removeComma(value: string): Promise<void> {
    dummy_payment_amount.value = String(value).replace(/,/g, '').replace(/¥/g, '');
  }

  // 金額フォーカスON
  async function onFocus(value: string): Promise<void> {
    await removeComma(value);
  }

  // 金額フォーカスアウト
  async function onBlur(value: string): Promise<void> {
    await removeComma(value);

    if (value) {
      const regexPattern = /^[0-9]+$/;
      if (!regexPattern.test(value)) {
        errdialog.value = true;
        errMessage.value = '数値およびカンマのみ入力してください';
        return;
      }
    }

    await commmaMoney(value);
  }

  return {
    deliveryKbnRadioItems: DELIVERY_KBN_RADIO_ITEMS,
    deliveryKbnSelectValues,
    dummy_payment_amount,
    isFormValid,
    onClickDeliveryKbn,
    onChangePaymentMethod,
    commmaMoney,
    removeComma,
    onFocus,
    onBlur,
  };
};
